import { type ReactNode, useCallback, useMemo, useRef } from "react";

import { Button, useBreakpoint } from "@backstage/ui";

import { CONTENT_ELEMENT_ID } from "../constants";
import { usePersistedPinState } from "../hooks";
import styles from "./AppLayout.module.css";
import { SidebarPinStateProvider } from "./sidebarPinState";

/**
 * The app shell: renders the nav rail alongside the routed page content.
 *
 * This replaces Backstage's `SidebarPage` from `@backstage/core-components`,
 * which is what the upstream `app/layout` extension renders by default. That
 * component is Material UI based, so keeping it would have made an MUI-free
 * sidebar impossible — the shell, not just the rail, had to be rebuilt. See
 * `module.tsx` for how the override is installed.
 *
 * Taking over the shell means taking over everything `SidebarPage` owned:
 *
 * - **Content offset.** The rail is fixed-position, so content is offset with
 *   a left pad that tracks the pinned width. See `AppLayout.module.css`.
 * - **Pin state.** Persisted to `localStorage` under the same key the old
 *   implementation used, so the preference survives this migration.
 * - **Skip-to-content.** `SidebarPage` exposed a content ref that
 *   `core-components`' sidebar used to render a skip link; that plumbing is
 *   internal to MUI components we no longer render, so the link lives here.
 *
 * The pin state is still published to upstream plugins, but through this
 * package's own {@link SidebarPinStateProvider} rather than the one in
 * `core-components` — see `sidebarPinState.tsx` for how it reaches them
 * without importing that package. It has to be published somehow:
 * `@backstage/plugin-search` calls `useSidebarPinState()` to hide its filter
 * column on narrow screens, and would otherwise fall back to the
 * `isMobile: false` default and render desktop filters on phones.
 */
export const AppLayout = (props: { nav: ReactNode; content: ReactNode }) => {
    const { nav, content } = props;
    const { down } = useBreakpoint();
    const contentRef = useRef<HTMLDivElement>(null);

    /*
     * Navigation switches to the mobile bottom bar below Backstage UI's `xs`
     * breakpoint, which is 640px. Note this is 40px wider than the sidebar it
     * replaced: `SidebarPage` used MUI v4's `breakpoints.down("xs")`, which is
     * `max-width: 599.95px`.
     *
     * The number is unavoidably duplicated, because a CSS media query cannot
     * read a custom property: `AppLayout.module.css` repeats it as
     * `@media (min-width: 640px)` to switch the content offset from the bottom
     * bar to the left rail. If you change one, change the other —
     * `AppLayout.breakpoint.test.tsx` pins both to the same value.
     *
     * This is the single source of truth for "is mobile" in the shell, and is
     * republished to plugins via the pin state context below.
     */
    const isMobile = down("xs");

    const [isPinned, toggleSidebarPinState] = usePersistedPinState();

    const pinState = useMemo(
        () => ({ isPinned, toggleSidebarPinState, isMobile }),
        [isPinned, toggleSidebarPinState, isMobile]
    );

    const focusContent = useCallback(() => contentRef.current?.focus(), []);

    return (
        <SidebarPinStateProvider value={pinState}>
            <div className={styles.root}>
                <div className={styles.skipLink}>
                    <Button variant="primary" onPress={focusContent}>
                        Skip to content
                    </Button>
                </div>
                {nav}
                <div
                    id={CONTENT_ELEMENT_ID}
                    ref={contentRef}
                    className={styles.content}
                    data-pinned={isPinned}
                    tabIndex={-1}
                >
                    {content}
                </div>
            </div>
        </SidebarPinStateProvider>
    );
};
